import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  WrenchScrewdriverIcon,
  PlusIcon,
  TrashIcon,
  CogIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input, StatCard, EmptyState } from '../components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// ============ Types ============

interface EquipmentItem {
  id: string;
  userId: string;
  farmId: string | null;
  name: string;
  type: string;
  make: string | null;
  model: string | null;
  purchaseDate: string | null;
  purchasePrice: number | null;
  status: string;
  fuelType: string;
  hoursUsed: number;
  lastServiceDate: string | null;
  nextServiceDate: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface EquipmentLog {
  id: string;
  equipmentId: string;
  logType: string;
  description: string | null;
  hours: number | null;
  cost: number | null;
  performedBy: string | null;
  logDate: string;
  createdAt: string;
}

interface MaintenanceScheduleItem {
  id: string;
  equipmentId: string;
  equipmentName: string;
  title: string;
  description: string | null;
  intervalHours: number | null;
  intervalDays: number | null;
  lastPerformed: string | null;
  nextDue: string | null;
  status: string;
}

interface EquipmentStats {
  totalEquipment: number;
  activeEquipment: number;
  totalValue: number;
  totalMaintenanceCost: number;
  totalHoursUsed: number;
  upcomingServices: number;
  overdueServices: number;
  byType: Array<{ type: string; count: number }>;
  recentCosts: Array<{ month: string; cost: number }>;
}

interface Farm {
  id: string;
  name: string;
}

// ============ Constants ============

const EQUIPMENT_TYPES = [
  { value: 'tractor', label: 'Tractor' },
  { value: 'plough', label: 'Plough' },
  { value: 'sprayer', label: 'Sprayer' },
  { value: 'harvester', label: 'Harvester' },
  { value: 'irrigation_pump', label: 'Irrigation Pump' },
  { value: 'drone', label: 'Drone' },
  { value: 'seeder', label: 'Seeder' },
  { value: 'other', label: 'Other' },
];

const FUEL_TYPES = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'petrol', label: 'Petrol' },
  { value: 'electric', label: 'Electric' },
  { value: 'manual', label: 'Manual' },
  { value: 'na', label: 'N/A' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  maintenance: 'bg-amber-100 text-amber-700',
  broken: 'bg-red-100 text-red-700',
  retired: 'bg-gray-100 text-gray-500',
};

const TYPE_ICONS: Record<string, string> = {
  tractor: '🚜',
  plough: '🔧',
  sprayer: '💦',
  harvester: '🌾',
  irrigation_pump: '💧',
  drone: '🛸',
  seeder: '🌱',
  other: '⚙️',
};

const PIE_COLORS = ['#16a34a', '#2563eb', '#d97706', '#dc2626', '#7c3aed', '#06b6d4', '#f59e0b', '#6b7280'];

// ============ Component ============

export default function Equipment() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'detail' | 'maintenance' | 'stats'>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form state
  const [form, setForm] = useState({
    name: '', type: 'tractor', farmId: '', make: '', model: '',
    purchaseDate: '', purchasePrice: '', status: 'active',
    fuelType: 'diesel', hoursUsed: '', lastServiceDate: '', nextServiceDate: '',
  });

  // Log form state
  const [logForm, setLogForm] = useState({ description: '', hours: '', cost: '', logDate: '' });

  // ============ Queries ============

  const { data: equipment = [], isLoading } = useQuery({
    queryKey: ['equipment', filterType, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      const res = await fetch(`${API_URL}/equipment?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json() as Promise<EquipmentItem[]>;
    },
  });

  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/farms`, { headers: authHeaders() });
      if (!res.ok) return [];
      return res.json() as Promise<Farm[]>;
    },
  });

  const { data: detail } = useQuery({
    queryKey: ['equipment-detail', selectedId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/equipment/${selectedId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Not found');
      return res.json() as Promise<{ equipment: EquipmentItem; recentLogs: EquipmentLog[] }>;
    },
    enabled: !!selectedId && activeTab === 'detail',
  });

  const { data: maintenanceSchedules = [] } = useQuery({
    queryKey: ['maintenance-schedule'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/equipment/maintenance`, { headers: authHeaders() });
      if (!res.ok) return [];
      return res.json() as Promise<MaintenanceScheduleItem[]>;
    },
    enabled: activeTab === 'maintenance',
  });

  const { data: stats } = useQuery({
    queryKey: ['equipment-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/equipment/stats`, { headers: authHeaders() });
      if (!res.ok) return null;
      return res.json() as Promise<EquipmentStats>;
    },
    enabled: activeTab === 'stats',
  });

  // ============ Mutations ============

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name: form.name,
        type: form.type,
        status: form.status,
        fuelType: form.fuelType,
      };
      if (form.farmId) body.farmId = form.farmId;
      if (form.make) body.make = form.make;
      if (form.model) body.model = form.model;
      if (form.purchaseDate) body.purchaseDate = form.purchaseDate;
      if (form.purchasePrice) body.purchasePrice = parseFloat(form.purchasePrice);
      if (form.hoursUsed) body.hoursUsed = parseFloat(form.hoursUsed);
      if (form.lastServiceDate) body.lastServiceDate = form.lastServiceDate;
      if (form.nextServiceDate) body.nextServiceDate = form.nextServiceDate;

      const res = await fetch(`${API_URL}/equipment`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to create');
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success('Equipment added!');
      setForm({ name: '', type: 'tractor', farmId: '', make: '', model: '', purchaseDate: '', purchasePrice: '', status: 'active', fuelType: 'diesel', hoursUsed: '', lastServiceDate: '', nextServiceDate: '' });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      setActiveTab('list');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/equipment/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to delete');
    },
    onSuccess: () => {
      toast.success('Equipment retired');
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      setActiveTab('list');
      setSelectedId(null);
    },
    onError: () => toast.error('Failed to retire equipment'),
  });

  const logUsageMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (logForm.description) body.description = logForm.description;
      if (logForm.hours) body.hours = parseFloat(logForm.hours);
      if (logForm.cost) body.cost = parseFloat(logForm.cost);
      if (logForm.logDate) body.logDate = logForm.logDate;

      const res = await fetch(`${API_URL}/equipment/${selectedId}/usage`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to log');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Usage logged!');
      setLogForm({ description: '', hours: '', cost: '', logDate: '' });
      queryClient.invalidateQueries({ queryKey: ['equipment-detail', selectedId] });
    },
    onError: () => toast.error('Failed to log usage'),
  });

  const logMaintenanceMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (logForm.description) body.description = logForm.description;
      if (logForm.hours) body.hours = parseFloat(logForm.hours);
      if (logForm.cost) body.cost = parseFloat(logForm.cost);
      if (logForm.logDate) body.logDate = logForm.logDate;

      const res = await fetch(`${API_URL}/equipment/${selectedId}/maintenance`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to log');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Maintenance logged!');
      setLogForm({ description: '', hours: '', cost: '', logDate: '' });
      queryClient.invalidateQueries({ queryKey: ['equipment-detail', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedule'] });
    },
    onError: () => toast.error('Failed to log maintenance'),
  });

  // ============ Helpers ============

  const openDetail = (id: string) => {
    setSelectedId(id);
    setActiveTab('detail');
  };

  const tabs = [
    { key: 'list', label: 'My Equipment', icon: WrenchScrewdriverIcon },
    { key: 'add', label: 'Add Equipment', icon: PlusIcon },
    { key: 'maintenance', label: 'Maintenance', icon: CogIcon },
    { key: 'stats', label: 'Statistics', icon: CurrencyDollarIcon },
  ] as const;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipment</h1>
          <p className="text-gray-500 mt-1">Manage your farm equipment, maintenance, and usage</p>
        </div>
        {activeTab !== 'add' && (
          <Button
            variant="primary"
            size="md"
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => setActiveTab('add')}
          >
            Add Equipment
          </Button>
        )}
      </div>

      {/* Tabs (not shown in detail view) */}
      {activeTab !== 'detail' && (
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-8">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === key
                  ? 'bg-white text-kenagri-700 shadow-soft'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* ============ Equipment List ============ */}
      {activeTab === 'list' && (
        <div>
          {/* Filters */}
          <div className="flex gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-kenagri-500 focus:ring-1 focus:ring-kenagri-500/20"
              >
                <option value="">All Types</option>
                {EQUIPMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-kenagri-500 focus:ring-1 focus:ring-kenagri-500/20"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="broken">Broken</option>
                <option value="retired">Retired</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-48 bg-gray-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : equipment.length === 0 ? (
            <EmptyState
              icon={WrenchScrewdriverIcon}
              title="No equipment"
              description="Add your farm equipment to track usage and maintenance."
              action={{ label: 'Add Equipment', onClick: () => setActiveTab('add') }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {equipment.map((eq) => (
                <button
                  key={eq.id}
                  onClick={() => openDetail(eq.id)}
                  className="text-left bg-white rounded-2xl border border-gray-100 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{TYPE_ICONS[eq.type] || '⚙️'}</span>
                        <div>
                          <h3 className="font-semibold text-gray-900">{eq.name}</h3>
                          <p className="text-xs text-gray-500 capitalize">{eq.type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[eq.status]}`}>
                        {eq.status}
                      </span>
                    </div>

                    <div className="space-y-1.5 text-sm text-gray-500">
                      {(eq.make || eq.model) && (
                        <p>{[eq.make, eq.model].filter(Boolean).join(' ')}</p>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="flex items-center gap-1">
                          <ClockIcon className="h-3.5 w-3.5" />
                          {eq.hoursUsed.toFixed(0)} hrs
                        </span>
                        {eq.nextServiceDate && (
                          <span className={`text-xs ${new Date(eq.nextServiceDate) < new Date() ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                            Service: {new Date(eq.nextServiceDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ Add Equipment ============ */}
      {activeTab === 'add' && (
        <Card variant="gradient">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-kenagri-500 to-kenagri-600 shadow-glow-green">
                <PlusIcon className="h-5 w-5 text-white" />
              </div>
              <CardTitle>Add New Equipment</CardTitle>
            </div>
          </CardHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Equipment Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., John Deere 5075E" />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all">
                {EQUIPMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Assign to Farm</label>
              <select value={form.farmId} onChange={(e) => setForm({ ...form, farmId: e.target.value })} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all">
                <option value="">-- Unassigned --</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>

            <Input label="Make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="e.g., John Deere" />
            <Input label="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="e.g., 5075E" />

            <Input label="Purchase Date" type="date" value={form.purchaseDate} onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })} />
            <Input label="Purchase Price (KES)" type="number" value={form.purchasePrice} onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })} placeholder="0.00" />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all">
                <option value="active">Active</option>
                <option value="maintenance">In Maintenance</option>
                <option value="broken">Broken</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fuel Type</label>
              <select value={form.fuelType} onChange={(e) => setForm({ ...form, fuelType: e.target.value })} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all">
                {FUEL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <Input label="Hours Used" type="number" value={form.hoursUsed} onChange={(e) => setForm({ ...form, hoursUsed: e.target.value })} placeholder="0" />
            <Input label="Last Service Date" type="date" value={form.lastServiceDate} onChange={(e) => setForm({ ...form, lastServiceDate: e.target.value })} />
            <Input label="Next Service Date" type="date" value={form.nextServiceDate} onChange={(e) => setForm({ ...form, nextServiceDate: e.target.value })} />
          </div>

          <div className="flex justify-end mt-6">
            <Button
              variant="primary"
              size="lg"
              onClick={() => createMutation.mutate()}
              disabled={!form.name || createMutation.isPending}
              leftIcon={createMutation.isPending ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <CheckCircleIcon className="h-5 w-5" />}
            >
              {createMutation.isPending ? 'Saving...' : 'Add Equipment'}
            </Button>
          </div>
        </Card>
      )}

      {/* ============ Equipment Detail ============ */}
      {activeTab === 'detail' && detail && (
        <div>
          <button
            onClick={() => { setActiveTab('list'); setSelectedId(null); }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to list
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Info Card */}
            <div className="lg:col-span-2">
              <Card variant="gradient">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{TYPE_ICONS[detail.equipment.type] || '⚙️'}</span>
                      <div>
                        <CardTitle>{detail.equipment.name}</CardTitle>
                        <p className="text-sm text-gray-500 capitalize">{detail.equipment.type.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${STATUS_COLORS[detail.equipment.status]}`}>
                        {detail.equipment.status}
                      </span>
                      <button
                        onClick={() => deleteMutation.mutate(detail.equipment.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Retire equipment"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </CardHeader>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Make', value: detail.equipment.make || '-' },
                    { label: 'Model', value: detail.equipment.model || '-' },
                    { label: 'Fuel', value: detail.equipment.fuelType },
                    { label: 'Hours Used', value: `${detail.equipment.hoursUsed.toFixed(0)} hrs` },
                    { label: 'Purchase Price', value: detail.equipment.purchasePrice ? `KES ${detail.equipment.purchasePrice.toLocaleString()}` : '-' },
                    { label: 'Purchase Date', value: detail.equipment.purchaseDate ? new Date(detail.equipment.purchaseDate).toLocaleDateString() : '-' },
                    { label: 'Last Service', value: detail.equipment.lastServiceDate ? new Date(detail.equipment.lastServiceDate).toLocaleDateString() : '-' },
                    { label: 'Next Service', value: detail.equipment.nextServiceDate ? new Date(detail.equipment.nextServiceDate).toLocaleDateString() : '-' },
                  ].map((item) => (
                    <div key={item.label} className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500">{item.label}</p>
                      <p className="font-medium text-gray-900 mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Logs */}
              <div className="mt-6">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
                {detail.recentLogs.length === 0 ? (
                  <p className="text-gray-400 text-sm">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.recentLogs.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100">
                        <div className={`p-1.5 rounded-lg ${log.logType === 'usage' ? 'bg-blue-100' : log.logType === 'maintenance' ? 'bg-amber-100' : 'bg-red-100'}`}>
                          {log.logType === 'usage' ? <ClockIcon className="h-4 w-4 text-blue-600" /> : <WrenchScrewdriverIcon className="h-4 w-4 text-amber-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 capitalize">{log.logType}</p>
                            <p className="text-xs text-gray-400">{new Date(log.logDate).toLocaleDateString()}</p>
                          </div>
                          {log.description && <p className="text-sm text-gray-500 mt-0.5">{log.description}</p>}
                          <div className="flex gap-3 mt-1 text-xs text-gray-400">
                            {log.hours != null && <span>{log.hours} hrs</span>}
                            {log.cost != null && <span>KES {log.cost.toLocaleString()}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Log Forms */}
            <div className="space-y-6">
              <Card variant="gradient">
                <CardHeader>
                  <CardTitle className="text-base">Log Usage</CardTitle>
                </CardHeader>
                <div className="space-y-3">
                  <Input label="Description" value={logForm.description} onChange={(e) => setLogForm({ ...logForm, description: e.target.value })} placeholder="Field ploughing" />
                  <Input label="Hours" type="number" value={logForm.hours} onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })} placeholder="0" />
                  <Input label="Fuel Cost (KES)" type="number" value={logForm.cost} onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} placeholder="0" />
                  <Input label="Date" type="date" value={logForm.logDate} onChange={(e) => setLogForm({ ...logForm, logDate: e.target.value })} />
                  <Button variant="primary" size="md" onClick={() => logUsageMutation.mutate()} disabled={logUsageMutation.isPending} className="w-full">
                    {logUsageMutation.isPending ? 'Logging...' : 'Log Usage'}
                  </Button>
                </div>
              </Card>

              <Card variant="gradient">
                <CardHeader>
                  <CardTitle className="text-base">Log Maintenance</CardTitle>
                </CardHeader>
                <div className="space-y-3">
                  <Input label="Description" value={logForm.description} onChange={(e) => setLogForm({ ...logForm, description: e.target.value })} placeholder="Oil change and filter" />
                  <Input label="Service Hours" type="number" value={logForm.hours} onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })} placeholder="0" />
                  <Input label="Cost (KES)" type="number" value={logForm.cost} onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} placeholder="0" />
                  <Input label="Date" type="date" value={logForm.logDate} onChange={(e) => setLogForm({ ...logForm, logDate: e.target.value })} />
                  <Button variant="secondary" size="md" onClick={() => logMaintenanceMutation.mutate()} disabled={logMaintenanceMutation.isPending} className="w-full">
                    {logMaintenanceMutation.isPending ? 'Logging...' : 'Log Maintenance'}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ============ Maintenance Schedule ============ */}
      {activeTab === 'maintenance' && (
        <div>
          {maintenanceSchedules.length === 0 ? (
            <EmptyState
              icon={CogIcon}
              title="No maintenance schedules"
              description="Maintenance schedules will appear here when equipment has scheduled services."
            />
          ) : (
            <div className="space-y-3">
              {maintenanceSchedules.map((ms) => (
                <div
                  key={ms.id}
                  className={`p-4 bg-white rounded-xl border ${ms.status === 'overdue' ? 'border-red-200 bg-red-50/50' : 'border-gray-100'} flex items-center justify-between`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${ms.status === 'overdue' ? 'bg-red-100' : ms.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'}`}>
                      {ms.status === 'overdue' ? (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                      ) : ms.status === 'completed' ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <CogIcon className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{ms.title}</p>
                      <p className="text-sm text-gray-500">{ms.equipmentName}</p>
                      {ms.description && <p className="text-xs text-gray-400 mt-0.5">{ms.description}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    {ms.nextDue && (
                      <p className={`text-sm font-medium ${ms.status === 'overdue' ? 'text-red-600' : 'text-gray-700'}`}>
                        {ms.status === 'overdue' ? 'Overdue: ' : 'Due: '}
                        {new Date(ms.nextDue).toLocaleDateString()}
                      </p>
                    )}
                    <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      ms.status === 'overdue' ? 'bg-red-100 text-red-700' :
                      ms.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {ms.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ Statistics ============ */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Equipment"
              value={stats.totalEquipment}
              icon={WrenchScrewdriverIcon}
              color="green"
            />
            <StatCard
              title="Total Value"
              value={`KES ${(stats.totalValue / 1000).toFixed(0)}K`}
              icon={CurrencyDollarIcon}
              color="blue"
            />
            <StatCard
              title="Total Hours"
              value={`${stats.totalHoursUsed.toFixed(0)} hrs`}
              icon={ClockIcon}
              color="purple"
            />
            <StatCard
              title="Overdue Services"
              value={stats.overdueServices}
              icon={ExclamationTriangleIcon}
              color="rose"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Maintenance Costs Chart */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Maintenance Costs (Last 6 Months)</CardTitle>
              </CardHeader>
              {stats.recentCosts.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.recentCosts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value: number) => [`KES ${value.toLocaleString()}`, 'Cost']}
                        contentStyle={{ borderRadius: '0.75rem', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      />
                      <Bar dataKey="cost" fill="#16a34a" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-gray-400 text-sm py-8 text-center">No cost data available</p>
              )}
            </Card>

            {/* Equipment by Type Pie Chart */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Equipment by Type</CardTitle>
              </CardHeader>
              {stats.byType.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.byType}
                        dataKey="count"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={({ type, count }) => `${type} (${count})`}
                      >
                        {stats.byType.map((_entry, index) => (
                          <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-gray-400 text-sm py-8 text-center">No equipment data</p>
              )}
            </Card>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-kenagri-50 to-white rounded-xl p-5 border border-kenagri-100">
              <p className="text-sm font-medium text-kenagri-600">Active Equipment</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.activeEquipment}</p>
              <p className="text-xs text-gray-500 mt-1">of {stats.totalEquipment} total</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-5 border border-amber-100">
              <p className="text-sm font-medium text-amber-600">Total Maintenance Cost</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">KES {stats.totalMaintenanceCost.toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
              <p className="text-sm font-medium text-blue-600">Upcoming Services</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.upcomingServices}</p>
              <p className="text-xs text-gray-500 mt-1">in next 30 days</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
